import { gql } from '@apollo/client';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardSection,
  CardSectionControl,
  CardSectionDescription,
  CardSectionTitle,
  CardTitle,
  Group,
} from '@apollo/orbit';
import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { LazyUrlModal } from 'src/components/common/LazyUrlModal';
import { PermissionWarning } from 'src/components/permissionGuards/permissionWarning/PermissionWarning';
import { Config } from 'src/lib/config/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { UserPermission } from 'src/lib/graphqlTypes/types';
import { useLDFlag } from 'src/lib/launchDarkly';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import {
  ApproversRequiredSection,
  ApproversRequiredSectionServiceFragment,
} from '../approversRequiredSection/ApproversRequiredSection';
import {
  DefaultReviewersSection,
  DefaultReviewersSectionServiceFragment,
} from '../defaultReviewersSection/DefaultReviewersSection';
import {
  ReApprovalRequirementsSection,
  ReApprovalRequirementsSectionServiceFragment,
} from '../reapprovalRequirementsSection/ReApprovalRequirementsSection';

import { ProposalPermissionsModal } from './proposalPermissionsModal/ProposalPermissionsModal';

export const ApprovalsAndPermissionsCardFragment = gql`
  fragment ApprovalsAndPermissionsCardFragment on Service {
    id
    minProposalRoles {
      create
      edit
    }
    ...DefaultReviewersSectionServiceFragment
    ...ApproversRequiredSectionServiceFragment
    ...ReApprovalRequirementsSectionServiceFragment
  }
  ${DefaultReviewersSectionServiceFragment}
  ${ApproversRequiredSectionServiceFragment}
  ${ReApprovalRequirementsSectionServiceFragment}
`;

export const ApprovalsAndPermissionsCard = ({
  graph,
}: {
  graph: GraphQLTypes.ApprovalsAndPermissionsCardFragment;
}) => {
  const location = useLocation();

  const shouldShowReApprovalRequirementsSection = useLDFlag(
    'nebula-reapproval-required-on-change',
  );

  const selectedCreateRoles = useMemo(() => {
    const rolesRanked: UserPermission[] = Config.orderedGraphRoles
      .slice()
      .reverse();

    const roleRank = rolesRanked.indexOf(graph.minProposalRoles.create);

    const roleDisplayNames = rolesRanked
      .slice(1)
      .map((val) => `${Config.roleNameByRoleEnum[val]}s`);

    return roleDisplayNames.slice(0, roleRank);
  }, [graph.minProposalRoles.create]);

  const selectedEditRoles = useMemo(() => {
    const rolesRanked: UserPermission[] = Config.orderedGraphRoles
      .slice()
      .reverse();

    const roleRank = rolesRanked.indexOf(graph.minProposalRoles.edit);

    const roleDisplayNames = rolesRanked
      .slice(1)
      .map((val) => `${Config.roleNameByRoleEnum[val]}s`);

    return roleDisplayNames.slice(0, roleRank);
  }, [graph.minProposalRoles.edit]);

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Permissions and approvals</CardTitle>
        </CardHeader>
        <CardBody>
          <CardSection className="flex-row">
            <Group orientation="vertical">
              <CardSectionTitle>Permissions</CardSectionTitle>
              <CardSectionDescription className="max-w-[760px]">
                Currently, <span className="font-semibold">Admins</span>
                {selectedCreateRoles.map((val, index) => (
                  <React.Fragment key={val}>
                    {index === selectedCreateRoles.length - 1 ? ' and ' : ', '}
                    <span className="font-semibold">{val}</span>
                  </React.Fragment>
                ))}{' '}
                on this graph can create proposals. Additionally,{' '}
                <span className="font-semibold">Admins</span>{' '}
                {selectedEditRoles.map((val, index) => (
                  <React.Fragment key={val}>
                    {index === selectedEditRoles.length - 1 ? ' and ' : ', '}
                    <span className="font-semibold">{val}</span>
                  </React.Fragment>
                ))}{' '}
                on this graph can edit proposals.
              </CardSectionDescription>
            </Group>
            <CardSectionControl>
              <PermissionWarning
                graphId={graph.id}
                graphPermissions="canManageProposalPermissions"
                childRenderer={(hasPermission) => (
                  <Button
                    variant="secondary"
                    isDisabled={!hasPermission}
                    as={Link}
                    to={setOverlayVisibleLocation({
                      location,
                      overlayName: Config.modals.editProposalPermissions,
                    })}
                  >
                    Change permissions
                  </Button>
                )}
              />
            </CardSectionControl>
          </CardSection>
          <DefaultReviewersSection graph={graph} />
          <ApproversRequiredSection graph={graph} />
          {shouldShowReApprovalRequirementsSection && (
            <ReApprovalRequirementsSection graph={graph} />
          )}
        </CardBody>
      </Card>
      <LazyUrlModal name={Config.modals.editProposalPermissions}>
        {({ closeModal, isOpen }) => (
          <ProposalPermissionsModal
            isOpen={isOpen}
            onClose={closeModal}
            minProposalRoles={graph.minProposalRoles}
          />
        )}
      </LazyUrlModal>
    </>
  );
};
